import { useState } from 'react';

import { Button } from './ui.js';

/**
 * Paginacao por cursor — o par que faltava nas listas do produto.
 *
 * A API pagina por keyset desde sempre (`nextCursor`), mas so as telas de
 * historico usavam. Encomendas pedia `limit=20` e Moradores `limit=50` sem
 * nenhum controle na tela: passava disso e a linha simplesmente nao existia
 * para o porteiro. Numa portaria movimentada isso e uma semana de encomendas
 * invisiveis.
 *
 * Cursor, e nao numero de pagina, porque e o que o backend oferece: `LIMIT
 * 10000, 20` faz o MySQL ler e descartar 10.000 linhas, e a lista de
 * encomendas e a tabela mais quente do banco. O preco de keyset e nao dar para
 * pular para a pagina 7 — o que ninguem faz numa lista ordenada por data.
 *
 * Guardamos a PILHA de cursores, e nao so o atual, porque o keyset so anda
 * para frente: sem a pilha, "voltar" exigiria uma consulta invertida ou
 * recomecar do zero.
 */

export interface CursorPages {
  /** Cursor da pagina atual — `null` na primeira. Vai para a query. */
  cursor: string | null;
  /** 1-based, so para exibicao. */
  page: number;
  hasPrevious: boolean;
  advance: (nextCursor: string) => void;
  back: () => void;
  reset: () => void;
}

export function useCursorPages(): CursorPages {
  const [stack, setStack] = useState<string[]>([]);

  return {
    cursor: stack[stack.length - 1] ?? null,
    page: stack.length + 1,
    hasPrevious: stack.length > 0,
    advance: (nextCursor) => setStack((current) => [...current, nextCursor]),
    back: () => setStack((current) => current.slice(0, -1)),
    reset: () => setStack([]),
  };
}

/**
 * Envolve um `setState` de filtro para zerar a paginacao junto.
 *
 * Trocar o filtro sem zerar deixa a tela na pagina 3 de uma lista que agora
 * tem uma pagina — e o cursor, que aponta para uma linha da lista ANTERIOR,
 * nao significa nada na nova. O resultado e uma tabela vazia que parece "nao
 * achei nada".
 */
export function resetting<T>(pages: CursorPages, setter: (value: T) => void) {
  return (value: T) => {
    setter(value);
    pages.reset();
  };
}

export function Pagination({
  pages,
  nextCursor,
  loading = false,
  previousLabel = 'Anteriores',
  nextLabel = 'Proximas',
}: {
  pages: CursorPages;
  /** O `nextCursor` da resposta atual. `null` = ultima pagina. */
  nextCursor: string | null;
  loading?: boolean;
  previousLabel?: string;
  nextLabel?: string;
}) {
  // Uma pagina so: a barra nao acrescenta nada e ainda ocupa uma faixa no pe
  // de toda lista curta.
  if (!pages.hasPrevious && !nextCursor) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-line-soft px-5 py-4 sm:px-6">
      <Button variant="ghost" disabled={!pages.hasPrevious} onClick={pages.back}>
        {previousLabel}
      </Button>
      <Button
        variant="ghost"
        loading={loading}
        disabled={!nextCursor}
        onClick={() => nextCursor && pages.advance(nextCursor)}
      >
        {nextLabel}
      </Button>

      <span className="ml-auto text-sm text-muted">
        Pagina <span className="font-bold tabular-nums text-fg">{pages.page}</span>
      </span>

      {/* Da pagina 3 em diante, voltar de um em um e trabalhoso e o atalho vale
          a largura que ocupa. */}
      {pages.page > 2 && (
        <Button variant="quiet" className="py-1.5" onClick={pages.reset}>
          Inicio
        </Button>
      )}
    </div>
  );
}
